/**
 * AppViewPage — TikTok-style vertical full-screen ad feed
 * Pulls from the same ads source as the main homepage feed.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuthContext } from '../../context/AuthContext';
import styles from './AppViewPage.module.css';

const API_BASE = 'https://imselling-marketplace-production.up.railway.app/api';
const MEDIA_BASE = 'https://imselling-marketplace-production.up.railway.app';

function getToken() {
  return (
    localStorage.getItem('authToken') ||
    localStorage.getItem('token') ||
    sessionStorage.getItem('authToken') ||
    sessionStorage.getItem('token') ||
    ''
  );
}

function resolveUrl(val) {
  if (!val) return null;
  if (typeof val === 'string') {
    if (val.startsWith('http')) return val;
    if (val.startsWith('/')) return `${MEDIA_BASE}${val}`;
    return null;
  }
  // object shape (raw API before transform)
  const src = val.image_url || val.url || val.image || val.video_url || val.video || val.src;
  return resolveUrl(src);
}

function resolveImage(ad) {
  // transformAdData sets: img (primary), primary_image, images as string[]
  const candidates = [
    ad.img,
    ad.primary_image,
    ad.image,
    ...(Array.isArray(ad.images)
      ? ad.images.map(i => typeof i === 'string' ? i : resolveUrl(i))
      : []),
  ];
  for (const c of candidates) {
    const url = resolveUrl(c);
    if (url && !url.includes('fallback') && !url.includes('placeholder')) return url;
  }
  return null;
}

function resolveVideo(ad) {
  // transformAdData sets: primary_video, videos as string[]
  const candidates = [
    ad.primary_video,
    ...(Array.isArray(ad.videos)
      ? ad.videos.map(v => typeof v === 'string' ? v : resolveUrl(v))
      : []),
    ad.video_url,
  ];
  for (const c of candidates) {
    const url = resolveUrl(c);
    if (url) return url;
  }
  return null;
}

function formatPrice(price, currency = 'KES') {
  if (!price && price !== 0) return null;
  return `${currency} ${Number(price).toLocaleString()}`;
}

// ── helpers: build all media items for a given ad ───────────────────────────
function buildMediaList(ad) {
  const items = [];
  const seen = new Set();

  const addItem = (type, url) => {
    if (!url || seen.has(url)) return;
    seen.add(url);
    items.push({ type, url });
  };

  // Videos first
  const videoCandidates = [
    ad.primary_video,
    ...(Array.isArray(ad.videos)
      ? ad.videos.map(v => (typeof v === 'string' ? v : resolveUrl(v)))
      : []),
    ad.video_url,
  ];
  for (const c of videoCandidates) {
    const url = resolveUrl(c);
    addItem('video', url);
  }

  // Then images — collect all unique image URLs
  const imageCandidates = [
    ad.img,
    ad.primary_image,
    ad.image,
    ...(Array.isArray(ad.images)
      ? ad.images.map(i => (typeof i === 'string' ? i : resolveUrl(i)))
      : []),
  ];
  for (const c of imageCandidates) {
    const url = resolveUrl(c);
    if (url && !url.includes('fallback') && !url.includes('placeholder')) {
      addItem('image', url);
    }
  }

  return items;
}

// ── Single Ad Card (full-screen slide) ──────────────────────────────────────
function AdCard({ ad, isActive, onOpenDetail, onLike, likedIds, isMuted, onToggleMute }) {
  const videoRef = useRef(null);
  const [mediaIndex, setMediaIndex] = useState(0);
  const [showControls, setShowControls] = useState(false);
  const [playing, setPlaying] = useState(true);
  const [progress, setProgress] = useState(0);          // 0–100
  const controlTimer = useRef(null);

  const mediaList = buildMediaList(ad);
  const currentMedia = mediaList[mediaIndex] || null;
  const isVideo = currentMedia?.type === 'video';
  const isLiked = likedIds.has(ad.id);

  const price = formatPrice(ad.price || ad.asking_price);
  const title = ad.name || ad.title || 'Untitled';
  const seller = ad.seller_name || ad.seller?.identifier || 'Seller';
  const location = ad.location || ad.town || '';
  const category = ad.category || '';
  const isVerified = ad.verified || ad.verification_status === 'verified' || false;

  // ── Sync muted state ────────────────────────────────────────────────────
  useEffect(() => {
    if (!videoRef.current) return;
    videoRef.current.muted = isMuted;
    videoRef.current.volume = isMuted ? 0 : 1;
  }, [isMuted]);

  // ── Play/pause as card enters/leaves viewport ───────────────────────────
  useEffect(() => {
    if (!videoRef.current) return;
    if (isActive && isVideo) {
      videoRef.current.muted = isMuted;
      videoRef.current.volume = isMuted ? 0 : 1;
      videoRef.current.play().catch(() => {
        videoRef.current.muted = true;
        videoRef.current.play().catch(() => {});
      });
      setPlaying(true);
    } else {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      setProgress(0);
    }
  }, [isActive, isVideo]);

  // Reset progress when switching media items
  useEffect(() => {
    setProgress(0);
    setPlaying(true);
  }, [mediaIndex]);

  // ── Video progress tracking ─────────────────────────────────────────────
  const handleTimeUpdate = () => {
    if (!videoRef.current) return;
    const { currentTime, duration } = videoRef.current;
    if (duration > 0) setProgress((currentTime / duration) * 100);
  };

  const handleVideoEnded = () => {
    setProgress(100);
    setPlaying(false);
  };

  // ── Scrub on progress bar click ─────────────────────────────────────────
  const handleProgressClick = (e) => {
    if (!videoRef.current) return;
    const bar = e.currentTarget;
    const rect = bar.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    const newTime = ratio * videoRef.current.duration;
    videoRef.current.currentTime = newTime;
    setProgress(ratio * 100);
  };

  const togglePlay = () => {
    if (!videoRef.current) return;
    clearTimeout(controlTimer.current);
    setShowControls(true);
    if (videoRef.current.paused) {
      videoRef.current.play();
      setPlaying(true);
    } else {
      videoRef.current.pause();
      setPlaying(false);
    }
    controlTimer.current = setTimeout(() => setShowControls(false), 2000);
  };

  // ── Carousel nav ────────────────────────────────────────────────────────
  const goPrev = (e) => {
    e.stopPropagation();
    setMediaIndex(i => Math.max(0, i - 1));
  };
  const goNext = (e) => {
    e.stopPropagation();
    setMediaIndex(i => Math.min(mediaList.length - 1, i + 1));
  };

  return (
    <div className={styles.adCard}>
      {/* ── Media background ── */}
      <div className={styles.mediaWrap} style={{ "--bg-img": currentMedia?.type !== "video" && currentMedia?.url ? `url(${currentMedia.url})` : "none" }}>
        {currentMedia ? (
          isVideo ? (
            <video
              ref={videoRef}
              key={currentMedia.url}
              className={styles.media}
              src={currentMedia.url}
              loop={false}
              muted
              playsInline
              onClick={togglePlay}
              onTimeUpdate={handleTimeUpdate}
              onEnded={handleVideoEnded}
              onError={() => setMediaIndex(i => i + 1)}
            />
          ) : (
            <img
              key={currentMedia.url}
              className={styles.media}
              src={currentMedia.url}
              alt={title}
              onError={() => setMediaIndex(i => i + 1)}
            />
          )
        ) : (
          <div className={styles.noMedia}>
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="3" width="18" height="18" rx="3"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <polyline points="21 15 16 10 5 21"/>
            </svg>
          </div>
        )}

        {/* ── Mute/Unmute button ── */}
        {isVideo && isActive && (
          <button
            className={styles.muteBtn}
            onClick={(e) => { e.stopPropagation(); onToggleMute(); }}
            aria-label={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                <path d="M11 5L6 9H2v6h4l5 4V5z"/>
                <line x1="23" y1="9" x2="17" y2="15" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                <line x1="17" y1="9" x2="23" y2="15" stroke="white" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                <path d="M11 5L6 9H2v6h4l5 4V5z"/>
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round"/>
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round"/>
              </svg>
            )}
          </button>
        )}

        {/* ── Play/pause overlay ── */}
        {isVideo && showControls && (
          <div className={styles.playOverlay} onClick={togglePlay}>
            {playing ? (
              <svg width="48" height="48" viewBox="0 0 24 24" fill="white">
                <rect x="6" y="4" width="4" height="16" rx="1"/>
                <rect x="14" y="4" width="4" height="16" rx="1"/>
              </svg>
            ) : (
              <svg width="48" height="48" viewBox="0 0 24 24" fill="white">
                <polygon points="5 3 19 12 5 21 5 3"/>
              </svg>
            )}
          </div>
        )}

        {/* ── Video progress bar ── */}
        {isVideo && (
          <div className={styles.progressBarWrap} onClick={handleProgressClick}>
            <div className={styles.progressBarTrack}>
              <div className={styles.progressBarFill} style={{ width: `${progress}%` }} />
              <div className={styles.progressBarThumb} style={{ left: `${progress}%` }} />
            </div>
          </div>
        )}

        {/* ── Carousel prev/next arrows ── */}
        {mediaList.length > 1 && (
          <>
            {/* Prev/Next buttons sit at bottom-left, well clear of right sidebar */}
            <div className={styles.carouselControls}>
              <button
                className={styles.carouselBtn}
                onClick={goPrev}
                disabled={mediaIndex === 0}
                aria-label="Previous media"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
                  <polyline points="15 18 9 12 15 6"/>
                </svg>
              </button>

              {/* Dot indicators inline between buttons */}
              <div className={styles.carouselDots}>
                {mediaList.map((_, idx) => (
                  <button
                    key={idx}
                    className={`${styles.carouselDot} ${idx === mediaIndex ? styles.carouselDotActive : ''}`}
                    onClick={(e) => { e.stopPropagation(); setMediaIndex(idx); }}
                    aria-label={`Media ${idx + 1}`}
                  />
                ))}
              </div>

              <button
                className={styles.carouselBtn}
                onClick={goNext}
                disabled={mediaIndex === mediaList.length - 1}
                aria-label="Next media"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </button>
            </div>
          </>
        )}

        {/* Gradient overlay */}
        <div className={styles.gradient} />

        {/* VIDEO badge */}
        {isVideo && (
          <div className={styles.videoBadge}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
              <polygon points="5 3 19 12 5 21 5 3"/>
            </svg>
            VIDEO
          </div>
        )}

        {/* Media counter badge when multiple */}
        {mediaList.length > 1 && (
          <div className={styles.mediaCounter}>
            {mediaIndex + 1} / {mediaList.length}
          </div>
        )}
      </div>

      {/* ── Right action sidebar ── */}
      <div className={styles.sidebar}>
        {/* Seller avatar */}
        <div className={styles.avatarWrap}>
          <div className={styles.avatar}>
            {seller[0]?.toUpperCase() || 'S'}
          </div>
        </div>

        {/* Like */}
        <button
          className={`${styles.actionBtn} ${isLiked ? styles.liked : ''}`}
          onClick={() => onLike(ad)}
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill={isLiked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
          </svg>
          <span>{ad.likes_count || 0}</span>
        </button>

        {/* Comment / View Detail */}
        <button className={styles.actionBtn} onClick={() => onOpenDetail(ad)}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          <span>View</span>
        </button>

        {/* Share */}
        <button
          className={styles.actionBtn}
          onClick={() => {
            if (navigator.share) {
              navigator.share({ title, url: `${window.location.origin}?ad=${ad.id}` });
            } else {
              navigator.clipboard.writeText(`${window.location.origin}?ad=${ad.id}`);
            }
          }}
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
          </svg>
          <span>Share</span>
        </button>

        {/* Bookmark / Save */}
        <button className={styles.actionBtn}>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
          </svg>
          <span>Save</span>
        </button>
      </div>

      {/* ── Bottom info overlay ── */}
      <div
        className={styles.infoOverlay}
        style={{ bottom: mediaList.length > 1 ? '70px' : '16px' }}
      >
        {category && <span className={styles.categoryTag}>{category}</span>}

        <div className={styles.sellerRow}>
          {isVerified && (
            <span className={styles.verifiedSellerTag}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Verified Seller
            </span>
          )}
          {location && (
            <span className={styles.locationTag}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
              </svg>
              {location}
            </span>
          )}
        </div>

        <h2 className={styles.adTitle}>{title}</h2>

        {price && <div className={styles.adPrice}>{price}</div>}

        <button className={styles.viewBtn} onClick={() => onOpenDetail(ad)}>
          View Listing
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </button>
      </div>
    </div>
  );
}

// ── AppViewPage ──────────────────────────────────────────────────────────────
export default function AppViewPage({ products = [], onClose, onOpenDetail, openLoginModal }) {
  const { isAuthenticated } = useAuthContext();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [likedIds, setLikedIds] = useState(new Set());
  const [isMuted, setIsMuted] = useState(true); // start muted for autoplay compliance
  const containerRef = useRef(null);
  const slideRefs = useRef([]);
  const touchStartY = useRef(null);

  // ── Load ads ──────────────────────────────────────────────────────────────
  useEffect(() => {
    // Use passed products first — no re-fetch needed
    if (products && products.length > 0) {
      setAds(products);
      setLoading(false);
      return;
    }

    // Fallback: fetch independently
    const fetch_ = async () => {
      try {
        const token = getToken();
        const headers = token ? { Authorization: `Token ${token}` } : {};
        const res = await fetch(`${API_BASE}/ads/`, { headers });
        if (!res.ok) throw new Error('fetch failed');
        const data = await res.json();
        const raw = Array.isArray(data) ? data : data?.results || data?.ads || [];
        setAds(raw);
      } catch {
        setAds([]);
      } finally {
        setLoading(false);
      }
    };
    fetch_();
  }, [products]);

  // ── Intersection observer — track active card ─────────────────────────────
  useEffect(() => {
    if (!ads.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = slideRefs.current.indexOf(entry.target);
            if (idx !== -1) setActiveIndex(idx);
          }
        });
      },
      { threshold: 0.7, root: containerRef.current }
    );

    slideRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [ads]);

  // ── Touch swipe ───────────────────────────────────────────────────────────
  const onTouchStart = (e) => { touchStartY.current = e.touches[0].clientY; };
  const onTouchEnd = (e) => {
    if (touchStartY.current === null) return;
    const diff = touchStartY.current - e.changedTouches[0].clientY;
    if (Math.abs(diff) < 40) return;
    const next = diff > 0
      ? Math.min(activeIndex + 1, ads.length - 1)
      : Math.max(activeIndex - 1, 0);
    slideRefs.current[next]?.scrollIntoView({ behavior: 'smooth' });
    touchStartY.current = null;
  };

  // ── Like handler ──────────────────────────────────────────────────────────
  const handleLike = useCallback(async (ad) => {
    if (!isAuthenticated) { openLoginModal?.(); return; }
    const token = getToken();
    const already = likedIds.has(ad.id);
    setLikedIds(prev => {
      const next = new Set(prev);
      already ? next.delete(ad.id) : next.add(ad.id);
      return next;
    });
    try {
      await fetch(`${API_BASE}/ads/${ad.id}/like/`, {
        method: already ? 'DELETE' : 'POST',
        headers: { Authorization: `Token ${token}` },
      });
    } catch { /* revert */ setLikedIds(prev => { const n = new Set(prev); already ? n.add(ad.id) : n.delete(ad.id); return n; }); }
  }, [isAuthenticated, likedIds, openLoginModal]);

  // ── Keyboard nav ──────────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowDown' || e.key === 'j') {
        const next = Math.min(activeIndex + 1, ads.length - 1);
        slideRefs.current[next]?.scrollIntoView({ behavior: 'smooth' });
      }
      if (e.key === 'ArrowUp' || e.key === 'k') {
        const prev = Math.max(activeIndex - 1, 0);
        slideRefs.current[prev]?.scrollIntoView({ behavior: 'smooth' });
      }
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeIndex, ads.length, onClose]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className={styles.root}>

      {/* Feed container */}
      {loading ? (
        <div className={styles.loadingWrap}>
          <div className={styles.spinner} />
          <p>Loading feed…</p>
        </div>
      ) : ads.length === 0 ? (
        <div className={styles.emptyWrap}>
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
          </svg>
          <p>No ads available</p>
        </div>
      ) : (
        <div
          ref={containerRef}
          className={styles.feed}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {ads.map((ad, i) => (
            <div
              key={ad.id}
              ref={(el) => { slideRefs.current[i] = el; }}
              className={styles.slide}
            >
              <AdCard
                ad={ad}
                isActive={i === activeIndex}
                onOpenDetail={onOpenDetail}
                onLike={handleLike}
                likedIds={likedIds}
                isMuted={isMuted}
                onToggleMute={() => setIsMuted(prev => !prev)}
              />
            </div>
          ))}
        </div>
      )}

      {/* Bottom nav bar removed — handled by main app layout */}
    </div>
  );
}
